/**
 * Structured errors for the audio-upload and transcription endpoints.
 *
 * The older thunks reject with `String(error)`, which is fine when the only thing the UI does is
 * put the text in a toast. It is not fine here: a 402 has to open a real upgrade prompt showing the
 * clip's duration against the plan's limit, and a 429 has to say how long the wait is. Those
 * fields have to survive the trip through `rejectWithValue`, so they are kept as data rather than
 * being flattened into a sentence.
 */

export interface ApiError {
  status: number;
  /** Human-readable; the worker sends it as `error`, occasionally as `message`. */
  message: string;
  /** 402 and the session 429 — the offending audio duration in seconds. */
  duration?: number;
  /** 402 and the session 429 — the ceiling that was hit, in seconds. Null when uncapped. */
  limit?: number | null;
  /** 402 only — e.g. `pro`. Deliberately absent from the 429s: no plan shortens a wait. */
  requiredPlan?: string;
  /** 429 only — seconds until the rate limit resets. */
  resetIn?: number;
  /**
   * The machine-readable code the newer endpoints send alongside the sentence — `account_exists`,
   * `resend_too_soon`, and so on. Branch on this, never on the message text.
   */
  code?: string;
  /**
   * Why the request was refused, on the endpoints that classify their own rejections:
   * `file_too_long` / `period_limit_exceeded` (402, a paywall), `session_limit_exceeded` /
   * `too_many_in_flight` (429, a wait), `duplicate_in_flight` (409 — this exact file is already
   * running, see `transcriptionId` below) and `missing_media_reference` / `invalid_video_editor`
   * (400s from `/convertxml` and `PATCH /user/profile`). The status alone does not separate a
   * paywall from a wait, and the three 429s are three different lengths of wait — branch on this.
   */
  reason?: string;
  /**
   * The offending field on a per-field 400 — `'audioPath'` / `'beepPath'` for
   * `missing_media_reference`, `'video_editor'` for `invalid_video_editor`. Point the user at it
   * rather than a generic toast.
   */
  field?: string;
  /** `resend_too_soon` and `session_limit_exceeded` — seconds until the next attempt is allowed. */
  retryAfterSeconds?: number;
  /** `session_limit_exceeded` only — ISO 8601 UTC, when the session budget resets. */
  sessionExpiresAt?: string | null;
  /** `too_many_in_flight` only — jobs the user already has running. */
  inFlight?: number;
  /** `duplicate_in_flight` only — the job already running against this audioId. */
  transcriptionId?: string;
  /**
   * `wordlist_not_found` (404 from `/convertxml`) — the selected word list ids that could not be
   * read, because they were deleted or belong to another account. The conversion is refused rather
   * than run with fewer words than asked for, so these are what the user has to deselect: mark them
   * in the picker rather than only saying the request failed.
   */
  missing?: string[];
  /**
   * `GET /audio/:id/parts` — the file the user re-picked is not the one this upload started with
   * (409). R2 would otherwise stitch parts from two recordings into one object and complete it
   * without complaining, so the only safe answer is to say so rather than carry on.
   */
  fileMismatch?: boolean;
  /**
   * `GET /audio/:id/parts` — explicitly `false` when the upload cannot be carried on at all: the
   * row predates resumable uploads (409), or the multipart upload is gone (410).
   *
   * Absent is not the same as `false`, and in particular the "this upload is already complete" 409
   * sends neither this nor `fileMismatch` — see docs/BACKEND_REQUESTS.md §17. Only stop offering
   * Resume when the server actually said so.
   */
  resumable?: boolean;
  /**
   * `fileMismatch` on a size mismatch — how big the file this upload started with was. Worth
   * showing: "you picked 812 MB, this upload was 1.4 GB" is what tells someone which copy to find.
   */
  expectedSize?: number;
  /** `POST /audio/:id/complete` (400) — up to 50 parts R2 is still missing. Re-PUT these. */
  missingParts?: number[];
  /** The true total behind `missingParts`, which is capped at 50. */
  missingCount?: number;
  /** Parts the whole file was cut into, for context on a `missingCount`. */
  partCount?: number;
  /**
   * Set by `uploadAudioFile` rather than by the server: the `pending` row a failed upload left
   * behind. That row is resumable, so the caller needs to know which one it is — to remember the
   * file handle against it, and to point the user at the Resume button that now applies.
   */
  audioId?: string;
}

interface ErrorBody {
  error?: string;
  message?: string;
  details?: string;
  duration?: number;
  limit?: number | null;
  requiredPlan?: string;
  resetIn?: number;
  code?: string;
  reason?: string;
  field?: string;
  inFlight?: number;
  transcriptionId?: string;
  missing?: string[];
  fileMismatch?: boolean;
  resumable?: boolean;
  missingParts?: number[];
  missingCount?: number;
  partCount?: number;
  expectedSize?: number;
  sessionExpiresAt?: string | null;
  /** The session 429 sends camelCase; the older signup 429 sends snake_case. Both are read. */
  retryAfterSeconds?: number;
  retry_after_seconds?: number;
}

/**
 * Builds an {@link ApiError} from a failed response. `bodyText` is the already-buffered body —
 * `apiFetch` hands one out, and reading it twice is what the buffering exists to allow.
 */
export function toApiError(status: number, bodyText: string, fallback: string): ApiError {
  let body: ErrorBody = {};

  try {
    body = JSON.parse(bodyText) as ErrorBody;
  } catch {
    // Not JSON — an HTML error page from a proxy, or an empty body. The raw text is still the
    // most useful thing we have.
    return {status, message: bodyText.trim() || fallback};
  }

  const message = body.error || body.message || bodyText.trim() || fallback;

  return {
    status,
    message: body.details && body.details !== message ? `${message} (${body.details})` : message,
    duration: body.duration,
    limit: body.limit,
    requiredPlan: body.requiredPlan,
    resetIn: body.resetIn,
    code: body.code,
    reason: body.reason,
    field: body.field,
    inFlight: body.inFlight,
    transcriptionId: body.transcriptionId,
    missing: body.missing,
    fileMismatch: body.fileMismatch,
    resumable: body.resumable,
    missingParts: body.missingParts,
    missingCount: body.missingCount,
    partCount: body.partCount,
    expectedSize: body.expectedSize,
    sessionExpiresAt: body.sessionExpiresAt,
    retryAfterSeconds: body.retryAfterSeconds ?? body.retry_after_seconds,
  };
}

/** Wraps a thrown JS error (network failure, aborted request) in the same shape. */
export function toNetworkError(error: unknown, fallback: string): ApiError {
  const message = error instanceof Error ? error.message : String(error);
  return {status: 0, message: message || fallback};
}

/** Type guard for the `unknown` a rejected thunk hands back. */
export function isApiError(value: unknown): value is ApiError {
  return typeof value === 'object'
    && value !== null
    && typeof (value as ApiError).status === 'number'
    && typeof (value as ApiError).message === 'string';
}

/** The message to show a user for anything a thunk rejected with. */
export function apiErrorMessage(value: unknown, fallback = 'Something went wrong'): string {
  if (isApiError(value)) {
    return value.message;
  }
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  return fallback;
}
